import { Sport } from "@fresh8/types/src/language";
import { getTranslation } from "@fresh8/ad-data-composition/src/lang";
import {
  COMPETITION_WINNER,
  FIRS_ROUND_LEADER,
  WIN_AND_EACH_WAY,
  TRYSCORER,
  RACE_WINNER,
  TOP_BATSMAN,
  TOP_BOWLER,
  FIRST_GOALSCORER,
  FIRST_TEAM_TO_SCORE
} from "../constants/market-types.js";

import { Market, Option, SportEvent } from "@fresh8/types/src/betData";
import { ComputedRef, computed } from "vue";

export function getSport(sport?: Sport): string {
  const translation = getTranslation(window.navigator.language.toLowerCase());
  return sport && translation.Sports[sport] ? translation.Sports[sport] : sport;
}

type MarketTypes =
  | typeof COMPETITION_WINNER
  | typeof FIRS_ROUND_LEADER
  | typeof WIN_AND_EACH_WAY
  | typeof TRYSCORER
  | "List Over/Under"
  | typeof RACE_WINNER
  | typeof TOP_BATSMAN
  | typeof TOP_BOWLER
  | "Round Betting"
  | typeof FIRST_GOALSCORER
  | typeof FIRST_TEAM_TO_SCORE;

type MappedMarketText = {
  [Value in MarketTypes]: Value extends "Tryscorer"
    ? {
        "American Football": string;
        "Rugby Union": string;
        "Rugby League": string;
      }
    : Value extends "List Over/Under"
    ? {
        Football: string;
        Baseball: string;
        Basketball: string;
        "Ice Hockey": string;
      }
    : string;
};

export function getMarketText(lang: string): MappedMarketText {
  const { MarketTexts } = getTranslation(lang);
  return {
    [COMPETITION_WINNER]: MarketTexts.ToWin,
    [FIRS_ROUND_LEADER]: MarketTexts.FirstScore,
    [WIN_AND_EACH_WAY]: MarketTexts.ToWin,
    [TRYSCORER]: {
      "American Football": MarketTexts.FirstTouchdown,
      "Rugby Union": MarketTexts.FirstTry,
      "Rugby League": MarketTexts.FirstTry
    },
    ["List Over/Under"]: {
      Football: MarketTexts.TotalGoals,
      Baseball: MarketTexts.TotalRuns,
      Basketball: MarketTexts.TotalPoints,
      "Ice Hockey": MarketTexts.TotalGoals
    },
    [RACE_WINNER]: MarketTexts.ToWin,
    [TOP_BATSMAN]: MarketTexts.TopBatsman,
    [TOP_BOWLER]: MarketTexts.TopBowler,
    ["Round Betting"]: "{name}",
    [FIRST_GOALSCORER]: MarketTexts.FirstScore,
    [FIRST_TEAM_TO_SCORE]: MarketTexts.FirstScore
  };
}

export function getMarketOptionText(
  market: Market,
  event: SportEvent,
  option: Option
): ComputedRef<string> | undefined {
  const sport = computed(() => event?.sport);
  const marketType = computed(() => market?.marketType);
  const name = computed(() => option?.name?.short);

  const lang = window.navigator.language.toLowerCase();
  const MarketTextTemplates = getMarketText(lang);

  switch (marketType.value) {
    case FIRST_GOALSCORER:
      return computed(() => {
        if (name.value.toLowerCase() === "no goalscorer")
          return "No Goalscorer";
        return MarketTextTemplates[marketType.value as string].replace(
          "{name}",
          name.value.trim()
        ) as string;
      });

    case COMPETITION_WINNER:
    case FIRS_ROUND_LEADER:
    case WIN_AND_EACH_WAY:
    case RACE_WINNER:
    case TOP_BATSMAN:
    case TOP_BOWLER:
    case FIRST_TEAM_TO_SCORE:
      return computed(() => {
        return MarketTextTemplates[marketType.value as string].replace(
          "{name}",
          name.value.trim()
        ) as string;
      });

    // nested sports
    case TRYSCORER:
    case "List Over/Under":
      return computed(() => {
        return MarketTextTemplates[marketType.value as string][
          sport.value
        ].replace("{name}", name.value.trim()) as string;
      });
  }
}

// GET POTENTIAL WINNINGS TRANSLATIONS
export function getPotentialWinningsTranslations(): string {
  return getTranslation(window.navigator.language.toLowerCase()).PromoTexts.Bet;
}
